import type { User } from '@/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Auth Pinia store for client-side authentication state.
 *
 * This store manages client state only - the actual auth state is managed
 * by TanStack Query's useAuth composable which fetches from the server.
 *
 * Use this store for:
 * - Caching user data for quick access
 * - Managing auth loading states
 * - Permission/ability checks
 */
export const useAuthStore = defineStore(
    'auth',
    () => {
        // State
        const user = ref<User | null>(null);
        const abilities = ref<string[]>([]);
        const roles = ref<string[]>([]);
        const isLoading = ref(false);
        const isInitialized = ref(false);

        // Getters
        const isAuthenticated = computed(() => user.value !== null);

        const displayName = computed(() => {
            if (!user.value) return '';
            return user.value.display_name || user.value.email;
        });

        const avatarUrl = computed(() => user.value?.avatar_url);

        const initials = computed(() => {
            if (!user.value) return '';
            return user.value.initials || user.value.email.charAt(0).toUpperCase();
        });

        const hasTwoFactorEnabled = computed(
            () => user.value?.two_factor_confirmed_at !== null
        );

        const hasVerifiedEmail = computed(
            () => user.value?.email_verified_at !== null
        );

        // Actions
        function setUser(userData: User | null) {
            user.value = userData;
        }

        function setAuthData(data: {
            user: User | null;
            abilities?: string[];
            roles?: string[];
        }) {
            user.value = data.user;
            abilities.value = data.abilities || [];
            roles.value = data.roles || [];
            isInitialized.value = true;
        }

        function clearAuth() {
            user.value = null;
            abilities.value = [];
            roles.value = [];
            isInitialized.value = false;
        }

        function setLoading(loading: boolean) {
            isLoading.value = loading;
        }

        /**
         * Check if the user has a specific ability.
         */
        function hasAbility(ability: string): boolean {
            if (!isAuthenticated.value) return false;

            // Super admin has all abilities
            if (roles.value.includes('super-admin')) return true;

            // Check specific ability
            return abilities.value.includes(ability);
        }

        /**
         * Check if the user has any of the specified abilities.
         */
        function hasAnyAbility(requiredAbilities: string[]): boolean {
            return requiredAbilities.some((ability) => hasAbility(ability));
        }

        /**
         * Check if the user has all of the specified abilities.
         */
        function hasAllAbilities(requiredAbilities: string[]): boolean {
            return requiredAbilities.every((ability) => hasAbility(ability));
        }

        /**
         * Check if the user has a specific role.
         */
        function hasRole(role: string): boolean {
            if (!isAuthenticated.value) return false;
            return roles.value.includes(role);
        }

        /**
         * Check if the user has any of the specified roles.
         */
        function hasAnyRole(requiredRoles: string[]): boolean {
            return requiredRoles.some((role) => hasRole(role));
        }

        /**
         * Check if the user is a super admin.
         */
        function isSuperAdmin(): boolean {
            return hasRole('super-admin');
        }

        /**
         * Check if the user is an admin (has admin or super-admin role).
         */
        function isAdmin(): boolean {
            return hasAnyRole(['admin', 'super-admin']);
        }

        return {
            // State
            user,
            abilities,
            roles,
            isLoading,
            isInitialized,

            // Getters
            isAuthenticated,
            displayName,
            avatarUrl,
            initials,
            hasTwoFactorEnabled,
            hasVerifiedEmail,

            // Actions
            setUser,
            setAuthData,
            clearAuth,
            setLoading,
            hasAbility,
            hasAnyAbility,
            hasAllAbilities,
            hasRole,
            hasAnyRole,
            isSuperAdmin,
            isAdmin,
        };
    },
    {
        persist: {
            key: 'fnl-auth',
            // Only persist specific properties (v3 uses paths instead of pick)
            paths: ['user', 'abilities', 'roles', 'isInitialized'],
        },
    }
);

export type AuthStore = ReturnType<typeof useAuthStore>;
